// src/pages/lms/LMSNotifications.jsx
// Send custom push notifications from portal to all or specific students
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import sendNotification, {
  notifyAnnouncement, notifyResultsPublished, notifyDatesheet,
} from '../../services/NotificationService';
import { Send, Bell, Users, Clock, CheckCircle, AlertCircle, Search } from 'lucide-react';

const NOTIF_TYPES = [
  { value:'general',      label:'General',      emoji:'📢' },
  { value:'assignment',   label:'Assignment',   emoji:'📋' },
  { value:'exam',         label:'Exam',         emoji:'📝' },
  { value:'result',       label:'Result',       emoji:'📊' },
  { value:'fee',          label:'Fee',          emoji:'💰' },
  { value:'announcement', label:'Announcement', emoji:'📣' },
  { value:'event',        label:'Event',        emoji:'🎉' },
  { value:'course',       label:'Course',       emoji:'📚' },
  { value:'timetable',    label:'Timetable',    emoji:'📅' },
  { value:'library',      label:'Library',      emoji:'📖' },
];

export default function LMSNotifications() {
  const { account, institution } = useLMSAuth();
  const [log,       setLog]       = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [sending,   setSending]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [tokenCount,setTokenCount]= useState(0);

  const [form, setForm] = useState({
    title:'', body:'', notification_type:'general',
    target:'all', // 'all' | 'class' | 'custom'
    target_class:'',
  });

  useEffect(() => { if (institution?.id) loadAll(); }, [institution?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Get notification log
      const { data: logs } = await supabase
        .from('lms_notification_log')
        .select('*')
        .eq('institution_id', institution.id)
        .order('created_at', { ascending:false })
        .limit(100);
      setLog(logs || []);

      // Count active FCM tokens for this institution's students
      const { data: profiles } = await supabase
        .from('profiles').select('id')
        .eq('institution_id', institution.id).eq('role','student');
      if (profiles?.length) {
        const ids = profiles.map(p => p.id);
        const { count } = await supabase
          .from('device_fcm_tokens')
          .select('*', { count:'exact', head:true })
          .in('user_id', ids).eq('is_active', true);
        setTokenCount(count || 0);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const send = async () => {
    if (!form.title.trim()) { setError('Title required'); return; }
    if (!form.body.trim())  { setError('Message body required'); return; }
    setSending(true); setError('');
    try {
      const result = await sendNotification({
        institution_id:    institution.id,
        title:             form.title,
        body:              form.body,
        notification_type: form.notification_type,
        data:              { screen: form.notification_type },
      });
      if (result.error) throw new Error(result.error);
      setSuccess(`✅ Sent to ${result.sent} device${result.sent!==1?'s':''}! ${result.failed>0?`(${result.failed} failed)`:''}`);
      setForm({ title:'', body:'', notification_type:'general', target:'all', target_class:'' });
      await loadAll();
      setTimeout(() => setSuccess(''), 6000);
    } catch(e) { setError(e.message || 'Send failed'); }
    finally { setSending(false); }
  };

  // Quick templates
  const TEMPLATES = [
    { label:'📋 Assignment Reminder', title:'📋 New Assignment Posted', body:'A new assignment has been posted. Check the Assignments section for details and deadline.' },
    { label:'📝 Exam Alert',          title:'📝 Exams Starting Soon',   body:'Your exams are starting soon. Please check the datesheet and prepare accordingly.' },
    { label:'📊 Results Published',   title:'📊 Results Are Out!',      body:'Your exam results have been published. Tap to view your result card and grades.' },
    { label:'💰 Fee Reminder',        title:'💰 Fee Due Soon',          body:'Your semester fee is due soon. Please pay via KuickPay to avoid late fines.' },
    { label:'🎉 Event Notice',        title:'🎉 Upcoming Event',        body:'There is an upcoming event at your institution. Check the Events section for details.' },
    { label:'📅 Timetable Update',    title:'📅 Timetable Updated',     body:'Your class timetable has been updated. Please check the latest schedule in the app.' },
  ];

  const filteredLog = log.filter(n =>
    !search.trim() ||
    n.title.toLowerCase().includes(search.toLowerCase()) ||
    n.body.toLowerCase().includes(search.toLowerCase()) ||
    (n.notification_type||'').toLowerCase().includes(search.toLowerCase())
  );

  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };

  // Summary stats
  const sentToday  = log.filter(n => new Date(n.created_at).toDateString() === new Date().toDateString()).length;
  const totalSent  = log.filter(n => n.status === 'sent').length;
  const totalFailed= log.filter(n => n.status === 'failed').length;

  return (
    <LMSLayout>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Push Notifications</h1>
          <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>
            {tokenCount} active devices registered · Send to all or specific students
          </p>
        </div>
      </div>

      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}<button onClick={()=>setError('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer', color:'#6B7280' }}>✕</button></div>}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:24 }}>
        {[
          { label:'Active Devices',  value:tokenCount,   color:'#4F8EF7', emoji:'📱' },
          { label:'Sent Today',      value:sentToday,    color:'#10B981', emoji:'📤' },
          { label:'Total Sent',      value:totalSent,    color:'#7C3AED', emoji:'✅' },
          { label:'Failed',          value:totalFailed,  color:'#EF4444', emoji:'❌' },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', padding:'14px 16px' }}>
            <div style={{ fontSize:22, marginBottom:6 }}>{s.emoji}</div>
            <div style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11, color:'#6B7280' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, alignItems:'start' }}>
        {/* Compose notification */}
        <div style={{ background:'#fff', borderRadius:18, border:'1px solid #E5E7EB', padding:24 }}>
          <div style={{ fontWeight:800, color:'#111827', fontSize:16, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
            <Bell size={18} color="#4F8EF7" /> Compose Notification
          </div>

          {/* Quick templates */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', letterSpacing:0.5, marginBottom:8 }}>QUICK TEMPLATES</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {TEMPLATES.map(t => (
                <button key={t.label} onClick={() => setForm(f => ({...f, title:t.title, body:t.body}))}
                  style={{ padding:'5px 10px', borderRadius:6, border:'1px solid #E5E7EB', background:'#F9FAFB', fontSize:11, fontWeight:600, color:'#374151', cursor:'pointer' }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Type selector */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Type</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {NOTIF_TYPES.map(t => (
                <button key={t.value} onClick={() => setForm(f=>({...f,notification_type:t.value}))}
                  style={{ padding:'5px 10px', borderRadius:6, border:`1.5px solid ${form.notification_type===t.value?'#4F8EF7':'#E5E7EB'}`, background:form.notification_type===t.value?'#EFF6FF':'#fff', color:form.notification_type===t.value?'#1D4ED8':'#6B7280', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                  {t.emoji} {t.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Title *</label>
            <input value={form.title} onChange={e=>setForm({...form,title:e.target.value})}
              placeholder="e.g. 📋 New Assignment Posted" style={inp} maxLength={65} />
            <div style={{ fontSize:10, color:'#9CA3AF', marginTop:3 }}>{form.title.length}/65 characters</div>
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Message *</label>
            <textarea value={form.body} onChange={e=>setForm({...form,body:e.target.value})}
              placeholder="Write your notification message here..." rows={4}
              style={{ ...inp, resize:'vertical' }} maxLength={200} />
            <div style={{ fontSize:10, color:'#9CA3AF', marginTop:3 }}>{form.body.length}/200 characters</div>
          </div>

          {/* Target */}
          <div style={{ background:'#F8FAFF', borderRadius:12, padding:14, marginBottom:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:8 }}>📣 Send To</div>
            <div style={{ display:'flex', gap:8 }}>
              {[
                { value:'all', label:`All Students (${tokenCount} devices)` },
              ].map(t => (
                <button key={t.value} onClick={() => setForm(f=>({...f,target:t.value}))}
                  style={{ flex:1, padding:'8px 12px', borderRadius:8, border:`2px solid ${form.target===t.value?'#4F8EF7':'#E5E7EB'}`, background:form.target===t.value?'#EFF6FF':'#fff', color:form.target===t.value?'#1D4ED8':'#374151', fontWeight:700, fontSize:12, cursor:'pointer' }}>
                  👥 {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          {(form.title || form.body) && (
            <div style={{ border:'1px solid #E5E7EB', borderRadius:14, padding:16, marginBottom:20, background:'#F9FAFB' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', marginBottom:8, letterSpacing:0.5 }}>PREVIEW</div>
              <div style={{ background:'#fff', borderRadius:12, padding:14, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', border:'1px solid #E5E7EB' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>🏫</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:800, color:'#111827', fontSize:13 }}>{form.title || 'Notification Title'}</div>
                    <div style={{ fontSize:12, color:'#6B7280', marginTop:3, lineHeight:1.4 }}>{form.body || 'Notification message...'}</div>
                    <div style={{ fontSize:10, color:'#9CA3AF', marginTop:4 }}>Just now · UNIVERSE LMS</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button onClick={send} disabled={sending || tokenCount === 0}
            style={{ width:'100%', padding:'14px 0', borderRadius:12, border:'none', background: tokenCount===0?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)', color: tokenCount===0?'#9CA3AF':'#fff', fontWeight:800, fontSize:15, cursor: sending||tokenCount===0?'not-allowed':'pointer', opacity:sending?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
            {sending ? (
              <><span style={{ fontSize:16 }}>⏳</span> Sending...</>
            ) : (
              <><Send size={18} /> Send to {tokenCount} device{tokenCount!==1?'s':''}</>
            )}
          </button>
          {tokenCount === 0 && (
            <div style={{ fontSize:12, color:'#EF4444', marginTop:8, textAlign:'center' }}>
              No devices registered yet. Students need to install the app and allow notifications.
            </div>
          )}
        </div>

        {/* Notification log */}
        <div>
          <div style={{ fontWeight:800, color:'#111827', fontSize:16, marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
            <Clock size={18} color="#7C3AED" /> Notification History
          </div>
          <div style={{ position:'relative', marginBottom:12 }}>
            <Search size={14} color="#9CA3AF" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} />
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search notifications..."
              style={{ ...inp, paddingLeft:32 }} />
          </div>
          {loading ? (
            <div style={{ textAlign:'center', padding:32, color:'#9CA3AF' }}>Loading...</div>
          ) : filteredLog.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
              <Bell size={36} color="#E5E7EB" style={{ marginBottom:10 }} />
              <div style={{ color:'#9CA3AF', fontSize:13 }}>No notifications sent yet</div>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:600, overflowY:'auto' }}>
              {filteredLog.map(n => {
                const typeInfo = NOTIF_TYPES.find(t => t.value === n.notification_type) || NOTIF_TYPES[0];
                return (
                  <div key={n.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', padding:'12px 16px', display:'flex', alignItems:'flex-start', gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:10, background: n.status==='sent'?'#F0FDF4':'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                      {typeInfo.emoji}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, color:'#111827', fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{n.title}</div>
                      <div style={{ fontSize:11, color:'#6B7280', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{n.body}</div>
                      <div style={{ display:'flex', gap:8, marginTop:4, flexWrap:'wrap' }}>
                        <span style={{ fontSize:10, fontWeight:700, color:typeInfo ? '#4F8EF7' : '#9CA3AF', background:'#EFF6FF', padding:'1px 7px', borderRadius:4 }}>{typeInfo.label}</span>
                        <span style={{ fontSize:10, color: n.status==='sent'?'#10B981':'#EF4444', fontWeight:700 }}>
                          {n.status==='sent'?'✅ Sent':'❌ Failed'}
                        </span>
                        <span style={{ fontSize:10, color:'#9CA3AF' }}>{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </LMSLayout>
  );
}